/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import {
  Alert,
  Button,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Portal,
  Snackbar,
  TextField,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import Styles from '../../css/CreateAccountPage.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import CreateAccountModal from '../modals/CreateAccountModal';
import LoadingModal from '../modals/LoadingModal';
import { passwordRequirementsMsg, maxEmailLength } from '../../common/constants';
import {
  isStrEmptyOrNull,
  isValidEmailAddress,
  isValidPassword,
  isValidReenterField,
} from '../../common/UtilityFunctions';
import { useCreateAccountContext } from '../../contexts/CreateAccountContext';

const CreateAccountForm = () => {
  const createAccountContext = useCreateAccountContext();

  const navigate = useNavigate();

  return (
    <>
      <EnvelopeWithBox className={Styles.container} blockName={'Create New Account'}>
        <form className={Styles.form} onSubmit={createAccountContext.validateFields}>
          {createAccountContext.status === 'loading' && (
            <LoadingModal
              open={createAccountContext.status === 'loading'}
              loadingMessage={createAccountContext.loadingMessage}
            />
          )}
          {createAccountContext.createAccountModalOpen && <CreateAccountModal />}
          <Portal>
            <Snackbar
              open={!!createAccountContext.statusAlert.message}
              onClose={() =>
                createAccountContext.setStatusAlert({
                  status: '',
                  message: '',
                })
              }
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              ClickAwayListenerProps={{ onClickAway: () => null }}
            >
              <Alert
                onClose={() =>
                  createAccountContext.setStatusAlert({
                    status: '',
                    message: '',
                  })
                }
                severity={createAccountContext.statusAlert.status as any}
                variant='filled'
                className={Styles.alert}
              >
                {createAccountContext.statusAlert.message}
              </Alert>
            </Snackbar>
          </Portal>
          <div className={Styles.flexCol}>
            <>
              <p>Please fill out the following form to register on this site.</p>
              <div className={Styles.gridContainer}>
                <div>
                  <TextField
                    fullWidth
                    size='small'
                    label='Email Address * '
                    placeholder='Email Address *'
                    error={createAccountContext.emailAddressError}
                    helperText={
                      createAccountContext.emailAddressError ? createAccountContext.emailAddressErrorMsg : ' '
                    }
                    value={createAccountContext.emailAddress}
                    inputProps={{
                      maxLength: maxEmailLength,
                    }}
                    onChange={(e) => {
                      createAccountContext.setEmailAddress(e.target.value);
                      createAccountContext.setEmailAddressError(!isValidEmailAddress(e.target.value));
                      createAccountContext.setEmailAddressErrorMsg(
                        isStrEmptyOrNull(e.target.value)
                          ? 'Please enter your Email Address.'
                          : 'Email Address provided is not valid.',
                      );
                      // do not display Re-enter Email error if the user hasn't changed the Re-enter field yet
                      createAccountContext.setReenterEmailAddressError(
                        !createAccountContext.reenterEmailAddressChanged
                          ? false
                          : !isValidReenterField(e.target.value, createAccountContext.reenterEmailAddress),
                      );
                    }}
                  />
                </div>
                <TextField
                  fullWidth
                  size='small'
                  label='Re-enter Email Address *'
                  placeholder='Re-enter Email Address *'
                  error={createAccountContext.reenterEmailAddressError}
                  helperText={
                    createAccountContext.reenterEmailAddressError
                      ? createAccountContext.reenterEmailAddressErrorMsg
                      : ' '
                  }
                  value={createAccountContext.reenterEmailAddress}
                  inputProps={{
                    maxLength: maxEmailLength,
                  }}
                  onChange={(e) => {
                    createAccountContext.setReenterEmailAddress(e.target.value);
                    createAccountContext.setReenterEmailAddressError(
                      !isValidReenterField(createAccountContext.emailAddress, e.target.value),
                    );
                    createAccountContext.setReenterEmailAddressErrorMsg(
                      isStrEmptyOrNull(e.target.value)
                        ? 'Please re-enter your Email Address.'
                        : 'Email Addresses do not match.',
                    );
                    createAccountContext.setReenterEmailAddressChanged(true);
                  }}
                />
                <div>
                  <TextField
                    fullWidth
                    size='small'
                    label='Password *'
                    placeholder='Password *'
                    error={createAccountContext.passwordError}
                    helperText={createAccountContext.passwordError ? createAccountContext.passwordErrorMsg : ' '}
                    value={createAccountContext.password}
                    type={createAccountContext.showPassword ? 'text' : 'password'}
                    onChange={(e) => {
                      createAccountContext.handleUpdatePassword(e.target.value);
                      createAccountContext.setPasswordError(!isValidPassword(e.target.value));
                      createAccountContext.setPasswordErrorMsg(
                        isStrEmptyOrNull(e.target.value)
                          ? 'Please enter your Password.'
                          : 'Password does not meet criteria.',
                      );
                      // do not display Re-enter Password error if the user hasn't changed the Re-enter field yet
                      createAccountContext.setReenterPasswordError(
                        !createAccountContext.reenterPasswordChanged
                          ? false
                          : !isValidReenterField(e.target.value, createAccountContext.reenterPassword),
                      );
                    }}
                    onFocus={(e) => {
                      createAccountContext.setPasswordFocus(true);
                    }}
                    onBlur={(e) => {
                      createAccountContext.setPasswordFocus(false);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={createAccountContext.handleClickShowPassword}
                            onMouseDown={createAccountContext.handleMouseDownPassword}
                          >
                            {createAccountContext.showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <TextField
                  fullWidth
                  size='small'
                  label='Re-enter Password *'
                  placeholder='Re-enter Password *'
                  error={createAccountContext.reenterPasswordError}
                  helperText={
                    createAccountContext.reenterPasswordError ? createAccountContext.reenterPasswordErrorMsg : ' '
                  }
                  value={createAccountContext.reenterPassword}
                  type={createAccountContext.showReenterPassword ? 'text' : 'password'}
                  onChange={(e) => {
                    createAccountContext.handleUpdateReenterPassword(e.target.value);
                    createAccountContext.setReenterPasswordError(
                      !isValidReenterField(createAccountContext.password, e.target.value),
                    );
                    createAccountContext.setReenterPasswordErrorMsg(
                      isStrEmptyOrNull(e.target.value) ? 'Please re-enter your Password.' : 'Passwords do not match.',
                    );
                    createAccountContext.setReenterPasswordChanged(true);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={createAccountContext.handleClickShowReenterPassword}
                          onMouseDown={createAccountContext.handleMouseDownReenterPassword}
                        >
                          {createAccountContext.showReenterPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <p className={Styles.text}>{passwordRequirementsMsg}</p>
              </div>
              <FormControlLabel
                control={
                  <input
                    required
                    type='checkbox'
                    checked={createAccountContext.certified}
                    onChange={(e) => {
                      createAccountContext.setCertified(!createAccountContext.certified);
                      createAccountContext.setCreateAccountModalOpen(!createAccountContext.certified);
                    }}
                  />
                }
                className={Styles.text}
                label='I certify that I am an applicant on a loan.'
              />
            </>
          </div>
          <Button onClick={() => navigate(-1)} size='large' className={Styles.backButton}>
            {' '}
            Back{' '}
          </Button>
          <Button type='submit' size='large' className={Styles.button}>
            {' '}
            Submit{' '}
          </Button>
        </form>
      </EnvelopeWithBox>
    </>
  );
};

export default CreateAccountForm;
